import {ProgressBar} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useState} from 'react'

import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {usePaginatedItemsQueryStatus} from '../../state-providers/data-refresh/use-paginated-items-query-status'
import {
  type StaleVariable,
  usePaginatedViewVariablesContext,
} from '../../state-providers/memex-items/context/paginated-view-variables'
import styles from './view-loading-progress-bar.module.css'

export function ViewLoadingProgressBar() {
  const {memex_reduce_view_shifts_slice_sort, memex_reduce_view_shifts_progress_bar} = useEnabledFeatures()
  const featureEnabled = memex_reduce_view_shifts_slice_sort && memex_reduce_view_shifts_progress_bar
  const {isFetching} = usePaginatedItemsQueryStatus()
  const {staleVariable} = usePaginatedViewVariablesContext() ?? {}
  const showProgress = isFetching && !!staleVariable
  const [progress, setProgress] = useState(0)

  // Delay when progress bar appears based on variable changed
  const shortDelayVariables: Array<StaleVariable> = ['sliceByValue', 'sliceByColumnId', 'sortedBy']
  const initialTimeout = staleVariable && shortDelayVariables.includes(staleVariable) ? 100 : 600
  useEffect(() => {
    if (!featureEnabled) return
    let mounted = true
    const timeout = setTimeout(() => {
      if (!mounted) return
      if (showProgress && progress === 0) {
        setProgress(5)
      }
    }, initialTimeout)
    return () => {
      mounted = false
      clearTimeout(timeout)
    }
  }, [showProgress, progress, featureEnabled, initialTimeout])

  useEffect(() => {
    if (!featureEnabled) return
    // Rapidly advance progress to completion when loading finishes
    const updateProgressTimeout = showProgress ? 50 : 10
    const timeout = setTimeout(() => {
      if (progress > 0 && progress < 100) {
        const increment = showProgress ? (progress < 90 ? 5 : 1) : 5
        const max = showProgress ? 95 : 100
        setProgress(prev => Math.min(prev + increment, max))
      }
      if (!showProgress && progress === 100) {
        setProgress(0)
      }
    }, updateProgressTimeout)
    return () => {
      clearTimeout(timeout)
    }
  }, [showProgress, progress, featureEnabled])
  if (!featureEnabled) return null
  return (
    <div className={clsx(styles.Box, progress > 0 ? styles.Loading : '')}>
      <ProgressBar barSize="small" bg="accent" progress={progress} animated className={styles.ProgressBar} />
    </div>
  )
}

try{ ViewLoadingProgressBar.displayName ||= 'ViewLoadingProgressBar' } catch {}